/**
 * One thing in a library entry that needs a decision: an icon, a title line,
 * at most a sentence, and the buttons that settle it. The drawer stacks these
 * above its tabs and shows none when the document is healthy.
 */
import type { LucideIcon } from "lucide-react";
import type { FC, ReactNode } from "react";

import { cn } from "@/lib/utils";

export type CalloutTone = "danger" | "warn" | "info";

const TONE: Record<CalloutTone, { box: string; icon: string }> = {
  danger: { box: "bg-danger-soft ring-danger-line", icon: "text-danger" },
  warn: { box: "bg-warn-soft ring-warn-line", icon: "text-warn" },
  info: { box: "bg-info-soft ring-info-line", icon: "text-info" }
};

type Props = {
  tone: CalloutTone;
  icon: LucideIcon;
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  "data-testid"?: string;
  "aria-label"?: string;
};

export const EntryCallout: FC<Props> = ({
  tone,
  icon: Icon,
  title,
  children,
  actions,
  ...rest
}) => (
  <section
    className={cn("flex gap-2.5 rounded-lg px-3 py-2.5 ring-1", TONE[tone].box)}
    {...rest}
  >
    <Icon className={cn("mt-0.5 size-4 shrink-0", TONE[tone].icon)} />
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <p className="text-base font-medium text-fg">{title}</p>
      {children ? (
        <div className="space-y-1.5 text-sm text-fg-2">{children}</div>
      ) : null}
      {actions ? (
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          {actions}
        </div>
      ) : null}
    </div>
  </section>
);
